import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, RotateCcw, Map } from 'lucide-react';
import type { VerifyResult } from '../../simulator';
import { SIM_FPS_VALUES } from '../../simulator';

/**
 * Résultat de la vérification déterministe (5 / 30 / 60 FPS) + état d'enregistrement de la
 * tentative (POST /api/exercises/:id/sim-result). Aucune IA.
 */

export interface SaveState {
  status: 'saving' | 'saved' | 'error';
  score?: number;
  isCorrect?: boolean;
  error?: string;
  missingKpCodes?: string[];
}

interface VerifyResultPanelProps {
  result: VerifyResult;
  save: SaveState | null;
  onRestart: () => void;
  backTo: string;
}

const fmt = (v: number) => v.toFixed(2).replace('.', ',');

export const VerifyResultPanel: React.FC<VerifyResultPanelProps> = ({ result, save, onRestart, backTo }) => {
  const ok = result.ok;
  return (
    <div
      id="sim-verify-result"
      data-testid="verify-result"
      className={`rounded-xl overflow-hidden border ${ok ? 'border-green-200 dark:border-green-800' : 'border-red-200 dark:border-red-800'} bg-white/70 dark:bg-gray-800/60`}
    >
      <div className={`px-4 py-3 flex items-center justify-between ${ok ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
        <div className="flex items-center space-x-3">
          {ok ? <CheckCircle className="w-6 h-6 text-green-500" /> : <XCircle className="w-6 h-6 text-red-500" />}
          <div>
            <h3 className={`text-base font-semibold ${ok ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}`}>
              {ok ? 'Bien fait : la vitesse ne dépend plus de la cadence !' : 'À corriger : le résultat n\'est pas encore le bon'}
            </h3>
            {save?.status === 'saved' && save.score !== undefined && (
              <p className={`text-sm ${ok ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                Score : {save.score}/100
              </p>
            )}
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${ok ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'}`}>
          {ok ? '✓ Réussi' : '⚠ À améliorer'}
        </span>
      </div>

      <div className="p-4 space-y-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide">
                <th className="py-1 pr-3">Cadence</th>
                <th className="py-1 pr-3">Images simulées</th>
                <th className="py-1 pr-3">Distance (3 s)</th>
                <th className="py-1">Distance / s</th>
              </tr>
            </thead>
            <tbody className="font-mono text-gray-800 dark:text-gray-100">
              {SIM_FPS_VALUES.map((fps) => {
                const m = result.perFps[fps];
                return (
                  <tr key={fps} className="border-t border-gray-200 dark:border-gray-700">
                    <td className="py-1 pr-3">{fps} FPS</td>
                    <td className="py-1 pr-3">{m.frames}</td>
                    <td className="py-1 pr-3">{fmt(m.distance)} u</td>
                    <td className="py-1">{m.error ? <span className="text-red-500">erreur l.{m.error.line}</span> : `${fmt(m.perSecond)} u/s`}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap gap-2 text-xs">
          <span className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
            Rapport 60 FPS / 5 FPS : <strong className="font-mono">{result.ratio60over5 === null ? '—' : `×${fmt(result.ratio60over5)}`}</strong>
          </span>
          <span className={`px-2 py-1 rounded-md ${result.usesDeltaTime ? 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-800 dark:text-cyan-200' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200'}`}>
            Time.deltaTime {result.usesDeltaTime ? 'détecté dans le déplacement' : 'absent du déplacement'}
          </span>
        </div>

        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed" data-testid="verify-reason">{result.reason}</p>

        <div className="border-t border-gray-200 dark:border-gray-700 pt-3 flex flex-wrap items-center gap-3 text-sm">
          {save?.status === 'saving' && (
            <span className="inline-flex items-center text-gray-600 dark:text-gray-300">
              <Loader2 className="w-4 h-4 mr-1 animate-spin" /> Enregistrement de la progression…
            </span>
          )}
          {save?.status === 'saved' && (
            <span className="inline-flex items-center text-green-700 dark:text-green-300" data-testid="progress-saved">
              <CheckCircle className="w-4 h-4 mr-1" /> Progression enregistrée
              {save.missingKpCodes && save.missingKpCodes.length > 0 && (
                <span className="ml-2 text-amber-600 dark:text-amber-300" title={save.missingKpCodes.join(', ')}>
                  (points de connaissance non trouvés en base — nœud crédité quand même)
                </span>
              )}
            </span>
          )}
          {save?.status === 'error' && (
            <span className="text-red-700 dark:text-red-300">
              Progression non enregistrée : {save.error ?? 'erreur réseau'}
            </span>
          )}
          <div className="flex-1" />
          <button
            type="button"
            onClick={onRestart}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <RotateCcw className="w-4 h-4 mr-1" /> Recommencer
          </button>
          <Link
            to={backTo}
            className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <Map className="w-4 h-4 mr-1" /> Retour au parcours
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VerifyResultPanel;
